import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {ReportStatusDTO} from '../model/reportStatusDTO';
import {QuestionDTO} from '../model/questionDTO';
import {PageReportOverViewDTO} from '../model/pageReportOverViewDTO';
import {SummaryDTO} from '../model/summaryDTO';
import {PreviewKeyDTO} from '../model/previewKeyDTO';
import {ReportInfoDTO} from '../model/reportInfoDTO';
import {StartDateDTO} from '../model/startDateDTO';
import {ReportMetaDataDTO} from '../model/reportMetaDataDTO';
import {ReportTypeCode} from '../model/reportTypeCode';
import * as moment from 'moment/moment';
import {EditableReportListItemDTO} from '../model/editableReportListItemDTO';


@Injectable({
  providedIn: 'root'
})
export class ReportService {

  static reportMap = new Map([
    [ReportTypeCode.Bb,   'boedelbeschrijving'],
    [ReportTypeCode.Rv,   'rekening en verantwoording'],
    [ReportTypeCode.Vje,  'vijfjaarlijkse evaulatie'],
    [ReportTypeCode.Erv,  'eindrekening en verantwoording'],
    [ReportTypeCode.Ervo, 'eindrekening en verantwoording na overlijden'],
  ]);

  constructor(private http: HttpClient) {
  }

  private readonly path = environment.baseUrl + environment.apiPrefix + 'report';

  static yearAppendix = (year: number) => year != null ? `year=${year}` : '';

  static getReportTypeFromUrl(name: string): ReportTypeCode {
    return this.getReportType(this.toNormalCase(name));
  }

  static getReportType(name: string): ReportTypeCode {
    return Array.from(this.reportMap.entries()).find(entry => entry[1] === name)?.[0];
  }

  static toNormalCase(str: string) {
    return str.trim()
      .split('-')
      .join(' ')
      .toLowerCase();
  }

  getQuestions(code) {
    return this.http.get<Array<QuestionDTO>>(`${this.path}/${code}/questions`);
  }

  getAnswers(code, clientId, year) {
    return this.http.get(`${this.path}/${code}/client/${clientId}?${ReportService.yearAppendix(year)}`);
  }

  getReportStatusList(clientId) {
    return this.http.get<Array<ReportStatusDTO>>(`${this.path}/client/${clientId}/status`);
  }

  generate(code, clientId, year) {
    return this.http.get(`${this.path}/${code}/generate?clientId=${clientId}&${ReportService.yearAppendix(year)}`, {
      responseType: 'blob',
      headers: new HttpHeaders().append('content-type', 'application/json')
    });
  }

  finish(code, clientId, year) {
    return this.http.get(`${this.path}/${code}/finish?clientId=${clientId}&${ReportService.yearAppendix(year)}`, {
      responseType: 'blob',
      headers: new HttpHeaders().append('content-type', 'application/json')
    });
  }

  downloadFinishedReport(finishedReportId) {
    return this.http.get(`${this.path}/download-finished/${finishedReportId}`, {
      responseType: 'blob',
      headers: new HttpHeaders().append('content-type', 'application/json')
    });
  }

  generatePreviewKey(clientId: number, code: string, year: number) {
    return this.http.get<PreviewKeyDTO>(`${this.path}/${code}/generate-preview-key?clientId=${clientId}&${ReportService.yearAppendix(year)}`);
  }

  getFinalReportList() {
    return this.http.get<Array<ReportStatusDTO>>(`${this.path}/final-list`);
  }

  create(clientId: number, code: ReportTypeCode, year?: number) {
    return this.http.get<void>(`${this.path}/client/${clientId}/create/${code}?${ReportService.yearAppendix(year)}`);
  }

  undoFinished(clientId: number, code: ReportTypeCode, year: number) {
    return this.http.get<void>(`${this.path}/client/${clientId}/undo-finished/${code}?${ReportService.yearAppendix(year)}`);
  }

  getEditableList(clientId: number) {
    return this.http.get<Array<EditableReportListItemDTO>>(`${this.path}/client/${clientId}/editable-list`);
  }

  close(clientId: number, code: ReportTypeCode, year: number) {
    return this.http.get<void>(`${this.path}/client/${clientId}/close/${code}?${ReportService.yearAppendix(year)}`);
  }

  canFinish(clientId: number, code: string, year: number) {
    return this.http.get<void>(`${this.path}/client/${clientId}/can-finish/${code}?${ReportService.yearAppendix(year)}`);
  }

  getSummary(clientId: number, code: string, year: number) {
    return this.http.get<SummaryDTO>(`${this.path}/client/${clientId}/summary/${code}?${ReportService.yearAppendix(year)}`);
  }

  save(clientId: number, reportTypeCode: string, year: number, map: {}) {
    return this.http.post<void>(`${this.path}/client/${clientId}/${reportTypeCode}/answer?${ReportService.yearAppendix(year)}`, map);
  }

  getAll(pageIndex, pageSize, column, dir) {
    const params = new HttpParams()
      .append('page', pageIndex)
      .append('size', pageSize)
      .append('sort', `${column},${dir}`);
    return this.http.get<PageReportOverViewDTO>(`${this.path}/overview`, {params: params});
  }

  getInfo(reportId: number) {
    return this.http.get<ReportInfoDTO>(`${this.path}/${reportId}/info`);
  }

  getReportMetaData(code: string, clientId: number, year: number) {
    return this.http.get<ReportMetaDataDTO>(`${this.path}/${code}/client/${clientId}/meta-data?${ReportService.yearAppendix(year)}`);
  }

  getNextReportStartDate(clientId) {
    return this.http.get<StartDateDTO>(`${this.path}/client/${clientId}/next-report-start-date`);
  }

  generateReportsNewYear() {
    return this.http.get<void>(`${this.path}/generate-new-reports`);
  }

  getReportName(type: ReportTypeCode): string {
    return ReportService.reportMap.get(type);
  }

  reportFileNameOf(code: ReportTypeCode, year, final) {
    return moment(new Date()).format('DD-MM-YYYY') + ' ' +
      (final ? 'definitieve' : 'concept') + ' ' +
      this.getReportName(code) +
      (year != null ? ' ' + year : '') +
      '.pdf';
  }

  getReportUrl(clientId, code: ReportTypeCode, year): string[] {
    return [`/bewind/${clientId}/rapportage/${this.toKebabCase(this.getReportName(code))}${year != null ? '/' + year : ''}`];
  }

  private toKebabCase(str: string) {
    return str.trim()
      .split(' ')
      .join('-')
      .toLowerCase();
  }
}
